import { useState, useEffect } from 'react'
import { onSnapshot, doc, type DocumentSnapshot } from 'firebase/firestore'
import { useAuth } from './useAuth'
import { db } from '../firebase'

// ─── Firestore document shape for a client ───────────────────────────────────
// Intentionally loose here — tighten as schema solidifies in production.

export interface HiredAgent {
  agentId: string
  hiredAt: string          // ISO timestamp
  onboardingComplete: boolean
  approvalMode: 'autopilot' | 'batch' | 'approve-each'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config: Record<string, any>
}

/**
 * Listings-presence snapshot mirror (backend PresenceCardModel). Each platform
 * is `present` (we handle it for ≥1 location) or `gated` with an honest reason.
 *
 * ⚠️ NOTHING RENDERS THIS ANY MORE (John, 2026-09-18) — the Control Center
 * "Your Presence" card was deleted along with the multi-platform promise it
 * showed. The type is kept only as the typed mirror of what the sweep still
 * persists onto the client doc, which is now a FORENSIC record: readable in the
 * console, read by no code. Do not re-render it without re-deciding the promise.
 */
export interface ListingsPresencePlatform {
  platform: string
  ownerAgent: string
  status: 'present' | 'gated'
  reason?: string
}
export interface ListingsPresenceSnapshot {
  presentCount: number
  gatedCount: number
  locationCount: number
  platforms: ListingsPresencePlatform[]
  sweptAtMs: number
}

/**
 * Multi-Location S5: physical address shape for a single business location.
 * Mirrors the canonical backend `LocationAddress` interface in
 * backend/functions/src/schema/types.ts. Display-only on the React side —
 * UI never validates / mutates these fields directly; mutations go through
 * the onboarding flow (S3a) or Ava intents that call the backend.
 */
export interface ClientLocationAddress {
  street1: string
  street2?: string
  city: string
  state: string
  postalCode: string
  /** ISO country code; defaults to 'US' for v1 (US-only at launch). */
  country?: string
}

/**
 * Multi-Location S5: single physical business location.
 *
 * Mirrors backend `Location` interface 1:1 — fields kept in lockstep so the
 * UI can render per-location data (location switcher, per-location detail
 * views, billing summaries) without re-shaping the document. `active: false`
 * locations are kept on the client for historical lookups but excluded from
 * per-location operations + billing per the `effectiveLocationCount` invariant.
 */
export interface ClientLocation {
  id: string
  name: string
  address: ClientLocationAddress
  /** GBP Place ID for THIS location's listing. */
  gbpPlaceId?: string
  gbpProfileUrl?: string
  gbpAccessGranted?: boolean
  gbpReviewCount?: number
  gbpRating?: number
  gbpKeywords?: string[]
  active: boolean
}

export interface ClientDocument {
  id: string
  businessName: string
  /** The client's own mark (top-level — written by the brand pipeline). */
  logoUrl?: string
  /** Their website, when they have one — used for the favicon fallback. */
  website?: string
  ownerName: string
  email: string
  phone: string
  trade: string
  city: string
  state: string
  serviceScope: 'local' | 'regional' | 'national'
  bilingual: boolean
  hiredAgents: Record<string, HiredAgent>
  promoCode: string | null
  referralCode: string
  referredBy: string | null
  stripeCustomerId: string | null
  /**
   * Referral credit EARNED but not yet applied, in USD cents. Positive = money
   * waiting for them.
   *
   * Written by billing/referralLedger.ts. It exists because the credit is capped
   * at what the invoice actually owes (John, 2026-07-19 — an invoice must never
   * show a negative total), so a client on a promo code can earn more credit
   * than their bill can absorb. Before this field was surfaced, that remainder
   * was invisible: Refer & Earn showed "activeCount × $75" — what they EARN —
   * while the invoice applied less, with nothing explaining the difference.
   *
   * A ledger written by a cron and read by nobody is exactly the bug the
   * referral system already had once. This is the read side.
   */
  referralCreditBalanceCents?: number
  /** Monthly recurring revenue in USD CENTS (backend/schema/types.ts). The client's real
   *  subscription amount — used so the Settings page states what they actually pay instead
   *  of the fabricated "$150 · paid" invoices it used to show. */
  mrr?: number
  /** Average job/ticket value in whole USD — the client's own figure, collected in
   *  onboarding and editable in Settings. Drives the dashboard "Est. Revenue" tile
   *  (booked × avgJobValue). Optional: when absent, the UI falls back to
   *  DEFAULT_AVG_JOB_VALUE and labels the estimate as a placeholder. */
  avgJobValue?: number
  monthlyBill: number
  onboardingComplete: boolean
  actionItems: ActionItem[]
  quietHoursStart: string   // e.g. "21:00"
  quietHoursEnd: string     // e.g. "07:00"
  /**
   * Multi-Location S5: optional list of physical business locations. Absent
   * (or empty) on every pre-multi-location client; populated when client
   * picks "Yes, multiple locations" in General Setup (onboarding S3b) or
   * adds locations post-onboarding via Ava. UI uses helpers in
   * `@bas/shared/multiLocation` to resolve display-time fallbacks
   * (single-loc clients still get a synthetic LocationSwitcher entry).
   */
  locations?: ClientLocation[]
  /**
   * Optional address fields — top-level legal/billing address that exists
   * on every client. For single-loc clients these double as the implicit
   * location 1; for multi-loc clients these remain the legal address only
   * (per-location addresses live on each `locations[]` entry).
   */
  address?: string
  gbpPlaceId?: string
  gbpProfileUrl?: string
  gbpAccessGranted?: boolean
  gbpReviewCount?: number
  gbpRating?: number
  /**
   * Listings-presence snapshot, persisted by the backend presence sweep
   * (agents/listings/presenceOrchestrator → buildPresenceCardModel).
   *
   * ⚠️ WRITE-ONLY as of 2026-09-18 — see the type above. The sweep that writes
   * it still does real work (it pushes NAP to GBP + Facebook); it is the CARD
   * that was the problem, not the sweep.
   */
  listingsPresence?: ListingsPresenceSnapshot
  createdAt: string
  updatedAt: string
  /**
   * White-Label Phase 1 (2026-05-23): franchise / partner network membership.
   * When set, the client inherits branding from `_franchise_/{id}/branding/config`
   * on top of BAS defaults. Same identifier used by invoicing Phase 10 for
   * franchise master templates. UI uses this to drive `useBranding()` hook.
   */
  franchiseNetworkId?: string

  /**
   * Pass-Through Billing data (L15877+L15878 wire-up, 2026-05-26).
   * Domain registrations + Workspace user subscriptions that BAS forwards
   * at zero markup. Mirrors the backend ClientDocument.passThroughBilling
   * field. Optional — older clients without pass-through state read as
   * undefined and the Settings UI hides the section.
   *
   * Field timestamps are ISO 8601 strings (matches backend serialization
   * for the React-side Firestore listener).
   */
  passThroughBilling?: {
    domains?: Array<{
      domain: string
      tld: string
      registeredAt: string
      expiresAt: string
      lastBilledFor?: string
      lastBilledAmountCents?: number
      lastBilledAt?: string
      gracePeriodStartedAt?: string | null
      expired?: boolean
    }>
    workspaceUsers?: Array<{
      email: string
      planTier?: 'starter' | 'standard' | 'plus'
      createdAt: string
      lastBilledFor?: string
      lastBilledAmountCents?: number
      lastBilledAt?: string
      gracePeriodStartedAt?: string | null
      suspended?: boolean
    }>
    lastUpdated?: string
  }
  /**
   * The client's Ava skill configuration ("configure Ava, don't force the bundle").
   * Edited post-onboarding from the Control Center Preferences tab (Settings.tsx →
   * /api/skill-preferences/update). Loosely typed here (the backend resolver in
   * onboarding/skillPreferences.ts is the SOT); the UI reads only the editable subset.
   */
  skillPreferences?: {
    customerTexting?: { appointmentReminders?: boolean; reviewRequests?: boolean; paymentFollowups?: boolean; bookingLinks?: boolean }
    booking?: 'ava' | 'hands-off'
    externalBookingUrl?: string
    inbox?: { enabled?: boolean; provider?: 'gmail' | 'outlook' | 'other' | 'unknown' }
    invoicing?: 'ava' | 'own'
    web?: { chatMode?: 'chat-and-texts' | 'chat-only' }
    nurture?: { channel?: 'email' | 'sms' | 'both'; ownEmailTool?: boolean }
    existingTools?: string[]
  }
}

export interface ActionItem {
  id: string
  label: string
  description: string
  agentId: string | null
  completedAt: string | null
  link: string | null
}

export interface ClientAuthState {
  user: import('firebase/auth').User | null
  clientId: string | null
  client: ClientDocument | null
  loading: boolean
  error: Error | null
  /** True when auth is resolved but user is not a clientAdmin */
  unauthorized: boolean
  /** True when a superAdmin is viewing this client via impersonation. */
  isImpersonating: boolean
  /** Impersonating in REVIEW mode — every write is refused server-side (CC review #24). */
  isReviewMode: boolean
}

const IMPERSONATE_STORAGE_KEY = 'cc-impersonate-client-id'

/**
 * Resolve the impersonation target. A superAdmin lands on the Control Center
 * with `?impersonate=<clientId>`; we persist it to sessionStorage so the choice
 * survives in-app navigation (sidebar links don't carry the query param). The
 * sessionStorage value clears on tab close, or via clearImpersonation().
 */
function getImpersonationClientId(): string | null {
  if (typeof window === 'undefined') return null
  const fromUrl = new URLSearchParams(window.location.search).get('impersonate')
  if (fromUrl) {
    try { window.sessionStorage.setItem(IMPERSONATE_STORAGE_KEY, fromUrl) } catch { /* ignore */ }
    return fromUrl
  }
  try { return window.sessionStorage.getItem(IMPERSONATE_STORAGE_KEY) } catch { return null }
}

/** Exit impersonation mode (clears the persisted target). */
export function clearImpersonation(): void {
  if (typeof window === 'undefined') return
  try { window.sessionStorage.removeItem(IMPERSONATE_STORAGE_KEY) } catch { /* ignore */ }
}

/**
 * Client-specific auth hook. Loads the Firestore client document via
 * onSnapshot for live updates. Returns `unauthorized: true` if the user
 * is authenticated but does not have the `clientAdmin` role.
 *
 * The consuming component is responsible for redirecting when
 * `unauthorized === true` or `!loading && !user`.
 */
/**
 * Super Admin impersonation: when a superAdmin opens the Control Center with
 * `?impersonate=<clientId>`, they view that client's live dashboard. Firestore
 * rules already grant superAdmin read access to every `clients/{id}` doc + its
 * subcollections, so this is purely a client-side scoping decision. The
 * ImpersonationBanner surfaces the mode. clientAdmins can never impersonate
 * (the role gate below excludes them).
 */
export function useClientAuth(): ClientAuthState {
  const { user, claims, loading: authLoading, error: authError } = useAuth()

  const [client, setClient] = useState<ClientDocument | null>(null)
  const [clientLoading, setClientLoading] = useState(true)
  const [clientError, setClientError] = useState<Error | null>(null)

  const impersonateId = getImpersonationClientId()
  const isImpersonating = claims.role === 'superAdmin' && !!impersonateId
  // The client whose data we actually load: the impersonated client for a
  // superAdmin, otherwise the clientAdmin's own claim.
  const effectiveClientId = isImpersonating ? impersonateId : claims.clientId

  useEffect(() => {
    // Wait for auth to resolve
    if (authLoading) return

    // Authorized to load a client doc when either: a clientAdmin viewing their
    // own account, or a superAdmin impersonating a specific client.
    const authorized = isImpersonating || (claims.role === 'clientAdmin' && !!effectiveClientId)
    if (!user || !authorized || !effectiveClientId) {
      setClientLoading(false)
      return
    }

    setClientLoading(true)

    const unsubscribe = onSnapshot(
      doc(db, 'clients', effectiveClientId),
      (snapshot: DocumentSnapshot) => {
        if (!snapshot.exists()) {
          setClientError(new Error(`Client document not found: ${effectiveClientId}`))
          setClient(null)
        } else {
          setClient({ id: snapshot.id, ...snapshot.data() } as ClientDocument)
          setClientError(null)
        }
        setClientLoading(false)
      },
      (error) => {
        setClientError(error)
        setClientLoading(false)
      }
    )

    return unsubscribe
  }, [user, claims.role, effectiveClientId, isImpersonating, authLoading])

  const loading = authLoading || clientLoading
  const error = authError ?? clientError
  // Impersonating superAdmins are authorized; everyone else needs clientAdmin.
  const unauthorized = !authLoading && !!user && !isImpersonating && claims.role !== 'clientAdmin'

  return {
    user,
    clientId: effectiveClientId,
    client,
    loading,
    error,
    unauthorized,
    isImpersonating,
    isReviewMode: isImpersonating && claims.reviewOnly === true,
  }
}
